import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { createAuthAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ConsentForm from '../components/client/ConsentForm';
import MotivationalQuote from '../components/client/MotivationalQuote';
import ExercisePlan from '../components/client/ExercisePlan';
import RecipesList from '../components/client/RecipesList';
import WeightTracker from '../components/client/WeightTracker';
import PhaseInfo from '../components/client/PhaseInfo';
import { LogOut, Home, Dumbbell, ChefHat, LineChart, Info, Scale } from 'lucide-react';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [currentWeight, setCurrentWeight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [productosRecomendados, setProductosRecomendados] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const loadData = async () => {
      try {
        const api = createAuthAPI();
        const profileResponse = await api.get('/client/profile', {
          signal: abortController.signal
        });
        
        if (isMounted) {
          setProfile(profileResponse.data);
          
          // Load current weight
          try {
            const weightResponse = await api.get('/client/weight-records', {
              signal: abortController.signal
            });
            if (weightResponse.data && weightResponse.data.length > 0) {
              setCurrentWeight(weightResponse.data[0].peso);
            }
          } catch (err) {
            // If no weight records, that's ok
          }

          // Load productos recomendados
          try {
            const productosResponse = await api.get('/client/productos-recomendados', {
              signal: abortController.signal
            });
            setProductosRecomendados(productosResponse.data || []);
          } catch (err) {
            // If no productos, that's ok
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          toast.error('Error al cargar el perfil');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  const loadProfile = async () => {
    try {
      const api = createAuthAPI();
      const response = await api.get('/client/profile');
      setProfile(response.data);
    } catch (error) {
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Sesión cerrada');
  };

  if (loading) {
    return (
      <div className="min-h-screen pharmacy-gradient flex items-center justify-center">
        <p className="text-lg text-gray-600">Cargando...</p>
      </div>
    );
  }

  // Show consent form if not signed
  if (profile && !profile.consentimiento_firmado) {
    return <ConsentForm onConsent={loadProfile} />;
  }

  return (
    <div className="min-h-screen pharmacy-gradient">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <img src="/logo.jpg" alt="Farmacia Tarin" className="h-12 w-auto flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-green-800 truncate">
                  {profile?.nombre} {profile?.apellidos}
                </h1>
                <p className="text-xs text-gray-600">Mi Seguimiento Nutricional</p>
                <div className="flex items-center gap-2 mt-1">
                  <img src="/siken.jpg" alt="Siken" className="h-6 object-contain" />
                  <img src="/fortepharma.jpg" alt="Fortepharma" className="h-6 object-contain" />
                  <img src="/therascience.jpg" alt="Therascience" className="h-6 object-contain" />
                </div>
              </div>
            </div>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-1 flex-shrink-0"
              data-testid="logout-button"
            >
              <LogOut className="h-3 w-3" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 bg-white/50 backdrop-blur p-1 rounded-xl">
            <TabsTrigger 
              value="home"
              className="flex items-center gap-2 data-[state=active]:bg-green-600 data-[state=active]:text-white"
              data-testid="home-tab"
            >
              <Home className="h-4 w-4" />
              Inicio
            </TabsTrigger>
            <TabsTrigger 
              value="exercises"
              className="flex items-center gap-2 data-[state=active]:bg-green-600 data-[state=active]:text-white"
              data-testid="exercises-tab"
            >
              <Dumbbell className="h-4 w-4" />
              Ejercicios
            </TabsTrigger>
            <TabsTrigger 
              value="recipes"
              className="flex items-center gap-2 data-[state=active]:bg-green-600 data-[state=active]:text-white"
              data-testid="recipes-tab"
            >
              <ChefHat className="h-4 w-4" />
              Recetas
            </TabsTrigger>
            <TabsTrigger 
              value="progress"
              className="flex items-center gap-2 data-[state=active]:bg-green-600 data-[state=active]:text-white"
              data-testid="progress-tab"
            >
              <LineChart className="h-4 w-4" />
              Progreso
            </TabsTrigger>
            <TabsTrigger 
              value="phase"
              className="flex items-center gap-2 data-[state=active]:bg-green-600 data-[state=active]:text-white"
              data-testid="phase-tab"
            >
              <Info className="h-4 w-4" />
              Mi Fase
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="space-y-6">
            <MotivationalQuote />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="pharmacy-card">
                <CardHeader>
                  <CardTitle className="text-green-800">Mi Fase Actual</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="phase-badge inline-block">
                      Fase {profile?.fase_actual}
                    </div>
                    <p className="text-sm text-gray-600 mt-4">
                      ¡Sigue adelante con tu programa! Explora las pestañas para ver tus ejercicios, recetas y progreso.
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="pharmacy-card">
                <CardHeader>
                  <CardTitle className="text-green-800">Peso Inicial</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-green-600">{profile?.peso_inicial} kg</p>
                  <p className="text-sm text-gray-600 mt-2">Registrado el {new Date(profile?.fecha_registro).toLocaleDateString('es-ES')}</p>
                </CardContent>
              </Card>
              <Card className="pharmacy-card bg-gradient-to-r from-blue-50 to-cyan-50">
                <CardHeader>
                  <CardTitle className="text-blue-800 flex items-center gap-2">
                    <Scale className="h-5 w-5" />
                    Peso Actual
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-blue-600">
                    {currentWeight ? `${currentWeight.toFixed(1)} kg` : `${profile?.peso_inicial} kg`}
                  </p>
                  {currentWeight && (
                    <p className="text-sm text-gray-600 mt-2">
                      Pérdida: {(profile?.peso_inicial - currentWeight).toFixed(1)} kg
                    </p>
                  )}
                </CardContent>
              </Card>
              <Card className="pharmacy-card bg-gradient-to-r from-purple-50 to-pink-50">
                <CardHeader>
                  <CardTitle className="text-purple-800">📊 Tu IMC</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-purple-600">
                    {profile && (currentWeight || profile.peso_inicial) && profile.altura 
                      ? ((currentWeight || profile.peso_inicial) / Math.pow(profile.altura / 100, 2)).toFixed(1)
                      : '-'}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    {profile && (currentWeight || profile.peso_inicial) && profile.altura && (
                      <>
                        {((currentWeight || profile.peso_inicial) / Math.pow(profile.altura / 100, 2)) < 18.5 && 'Peso bajo'}
                        {((currentWeight || profile.peso_inicial) / Math.pow(profile.altura / 100, 2)) >= 18.5 && 
                         ((currentWeight || profile.peso_inicial) / Math.pow(profile.altura / 100, 2)) < 25 && 'Peso normal ✓'}
                        {((currentWeight || profile.peso_inicial) / Math.pow(profile.altura / 100, 2)) >= 25 && 
                         ((currentWeight || profile.peso_inicial) / Math.pow(profile.altura / 100, 2)) < 30 && 'Sobrepeso'}
                        {((currentWeight || profile.peso_inicial) / Math.pow(profile.altura / 100, 2)) >= 30 && 'Obesidad'}
                      </>
                    )}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Productos Recomendados - TABLA */}
            {productosRecomendados.length > 0 && (
              <Card className="pharmacy-card border-2 border-green-200">
                <CardHeader>
                  <CardTitle className="text-green-800">💊 Productos y Suplementos Recomendados</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Productos que te recomiendo para apoyar tu progreso:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="p-3 border text-left font-semibold">Fecha Visita</th>
                          <th className="p-3 border text-left font-semibold">Producto/Suplemento</th>
                          <th className="p-3 border text-left font-semibold">Dosis</th>
                          <th className="p-3 border text-left font-semibold">Indicaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productosRecomendados.map((producto) => (
                          <tr key={producto.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 border whitespace-nowrap">
                              <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                                {new Date(producto.fecha_visita).toLocaleDateString('es-ES')}
                              </span>
                            </td>
                            <td className="p-3 border font-bold text-green-800">{producto.nombre}</td>
                            <td className="p-3 border">{producto.dosis || '-'}</td>
                            <td className="p-3 border">{producto.indicaciones || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {productosRecomendados.some(p => p.notas) && (
                    <div className="mt-3 p-3 bg-blue-50 rounded">
                      <p className="text-xs text-blue-700 font-semibold mb-2">📝 Notas adicionales:</p>
                      {productosRecomendados.filter(p => p.notas).map((producto) => (
                        <p key={producto.id} className="text-xs text-gray-600 mb-1">
                          • <strong>{producto.nombre}:</strong> {producto.notas}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="exercises">
            <ExercisePlan />
          </TabsContent>

          <TabsContent value="recipes">
            <RecipesList />
          </TabsContent>

          <TabsContent value="progress">
            <WeightTracker profile={profile} />
          </TabsContent>

          <TabsContent value="phase">
            <PhaseInfo />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClientDashboard;